// @ts-strict-ignore
import { LabelTextureDataService } from './label-texture-data.service';
import { LabelTextureDataVO, LabelTextureDescription, LabelTextureDescriptionVO, LabelTextureRequestVO, TextureCacheEntry } from '@3d-scene/processor/texture.vo';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ArrayUtils } from '@shared/util/array-utils';
import { Dictionary } from 'typescript-collections';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Log } from '@shared/debug/log';
import { Injectable } from '@angular/core';
import { StringUtils } from '@shared/util/string-utils';

class RequestEntry {
  public textureRequest: LabelTextureRequestVO;
  public textureData: LabelTextureDataVO;
  public cloneRequests: CloneRequestData[] = [];
}

class CloneRequestData {
  public requestId: string;
  public identity: MetaElementUsageIdentityVO;
}

class Request {
  public id: string;
  public identity: MetaElementUsageIdentityVO;
  public labelDescriptions: LabelTextureDescriptionVO[];
}

@Injectable({ providedIn: 'root' })
export class TextureCacheManager {
  private isCacheEnabled: boolean;
  private requestMap: Dictionary<string, RequestEntry> = new Dictionary<string, RequestEntry>();

  constructor(
    private readonly modelConversionService: ModelConversionService,
    private readonly labelTextureDataService: LabelTextureDataService,
  ) {
    this.init();
  }

  public init(): void {
    if (typeof Storage === 'undefined') {
      this.isCacheEnabled = false;
      Log.info('Warn: TextureCacheManager: Storage is not supported, textures will not be cached.');
    } else {
      this.isCacheEnabled = true;
    }
  }

  private get storage(): Storage {
    return sessionStorage;
  }

  public getLabelTextures(groupedLabelDescriptions: LabelTextureDescription[][]): Observable<LabelTextureDataVO[]> {
    const labelRequests: Request[] = this.createLabelRequests(groupedLabelDescriptions);
    if (ArrayUtils.isEmpty(labelRequests)) {
      return of([]);
    }
    return this.generateTextureRequest(labelRequests);
  }

  private createLabelRequests(groupedDescriptions: LabelTextureDescription[][]): Request[] {
    if (ArrayUtils.isEmpty(groupedDescriptions)) {
      return [];
    }
    const labelRequests: Request[] = [];
    for (const group of groupedDescriptions) {
      if (ArrayUtils.isNotEmpty(group)) {
        const identity: MetaElementUsageIdentityVO = this.findMetaElementUsageIdentity(group);
        const labelDescriptions: LabelTextureDescriptionVO[] = this.convertToLabelTextureDescriptionVO(group);
        if (identity && ArrayUtils.isNotEmpty(labelDescriptions)) {
          const request: Request = new Request();
          request.identity = identity;
          request.labelDescriptions = labelDescriptions;
          labelRequests.push(request);
        } else {
          Log.info('Error: TextureCacheManager: label descriptions are invalid, texture request will be ignored!');
        }
      }
    }
    return labelRequests;
  }

  private convertToLabelTextureDescriptionVO(labelDescriptions: LabelTextureDescription[]): LabelTextureDescriptionVO[] {
    const labelDescriptionVOs: LabelTextureDescriptionVO[] = [];
    let order: number = labelDescriptions.length > 1 ? 0 : NaN;
    for (const description of labelDescriptions) {
      const labelDescriptionVO: LabelTextureDescriptionVO = new LabelTextureDescriptionVO();
      if (!isNaN(order)) {
        labelDescriptionVO.order = order;
        order++;
      }
      labelDescriptionVO.height = description.height;
      labelDescriptionVO.width = description.width;
      labelDescriptionVO.keepAspect = description.keepAspect;
      labelDescriptionVO.distanceToPreviousConnection = description.distanceToPreviousConnection;
      labelDescriptionVO.borderData = description.borderData;
      labelDescriptionVO.textValue = description.textureAreaUsageVO.textValue;
      labelDescriptionVOs.push(labelDescriptionVO);
    }
    return labelDescriptionVOs;
  }

  private findMetaElementUsageIdentity(labelDescriptions: LabelTextureDescription[]): MetaElementUsageIdentityVO {
    const firstLabelDescription: LabelTextureDescription = labelDescriptions[0];
    return firstLabelDescription.textureAreaUsageVO.identity;
  }

  private generateTextureRequest(labelTextureRequests: Request[]): Observable<LabelTextureDataVO[]> {
    const generatedTextures: LabelTextureDataVO[] = [];
    const notCachedLabelTextureRequests: Request[] = [];
    for (const labelTextureRequest of labelTextureRequests) {
      const textureData: string = this.findCachedLabelTextureData(labelTextureRequest);
      if (textureData) {
        const labelTextureData: LabelTextureDataVO = new LabelTextureDataVO();
        labelTextureData.textureData = textureData;
        labelTextureData.identity = labelTextureRequest.identity;
        generatedTextures.push(labelTextureData);
      } else {
        notCachedLabelTextureRequests.push(labelTextureRequest);
      }
    }
    return this.excludeEqualServerRequests(notCachedLabelTextureRequests).pipe(
      map((serverSideTextures: LabelTextureDataVO[]) => {
        ArrayUtils.addAll(generatedTextures, serverSideTextures);
        return generatedTextures;
      })
    );
  }

  private excludeEqualServerRequests(labelTextureRequests: Request[]): Observable<LabelTextureDataVO[]> {
    const requests: Request[] = [];
    const textureRequests: Request[] = labelTextureRequests.concat();
    while (textureRequests.length > 0) {
      const request: Request = textureRequests.shift();
      request.id = StringUtils.createUuid();
      const requestsToRemove: Request[] = [];
      for (const requestToCompare of textureRequests) {
        if (ArrayUtils.equals(request.labelDescriptions, requestToCompare.labelDescriptions)) {
          requestToCompare.id = request.id;
          this.addCloneData(requestToCompare);
          requestsToRemove.push(requestToCompare);
        }
      }
      for (const requestToRemove of requestsToRemove) {
        textureRequests.splice(textureRequests.indexOf(requestToRemove), 1);
      }
      requests.push(request);
    }
    return this.createServerRequest(requests);
  }

  private addCloneData(request: Request): void {
    const cloneRequestData: CloneRequestData = new CloneRequestData();
    cloneRequestData.identity = request.identity;
    const labelTextureRequest: LabelTextureRequestVO = this.convertToLabelTextureRequestVO(request);
    this.addTextureDataMapping(labelTextureRequest, cloneRequestData);
  }

  private convertToLabelTextureRequestVO(request: Request): LabelTextureRequestVO {
    const labelTextureRequest: LabelTextureRequestVO = new LabelTextureRequestVO();
    labelTextureRequest.id = request.id;
    labelTextureRequest.labelTextureDescriptions = request.labelDescriptions;
    return labelTextureRequest;
  }

  private createServerRequest(requests: Request[]): Observable<LabelTextureDataVO[]> {
    if (ArrayUtils.isEmpty(requests)) {
      return of([]);
    }
    const requestIdToLabelTextureRequestMapping: Dictionary<string, LabelTextureRequestVO> = new Dictionary<string, LabelTextureRequestVO>();
    const requestIdToRequestMapping: Dictionary<string, Request> = new Dictionary<string, Request>();
    requests.forEach((request: Request) => {
      const labelTextureRequest: LabelTextureRequestVO = this.convertToLabelTextureRequestVO(request);
      requestIdToLabelTextureRequestMapping.setValue(labelTextureRequest.id, labelTextureRequest);
      requestIdToRequestMapping.setValue(request.id, request);
    });
    return this.labelTextureDataService.generateLabelTextures(requestIdToLabelTextureRequestMapping.values()).pipe(
      map((labelTextureDataArray: LabelTextureDataVO[]) => {
        for (const labelTextureData of labelTextureDataArray) {
          const connectionIdentity: MetaElementUsageIdentityVO = requestIdToRequestMapping.getValue(labelTextureData.id).identity;
          labelTextureData.identity = connectionIdentity;
          const labelTextureRequest: LabelTextureRequestVO = requestIdToLabelTextureRequestMapping.getValue(labelTextureData.id);
          this.addToCache(labelTextureRequest, labelTextureData);
        }
        return requestIdToRequestMapping.keys();
      }),
      map((requestIds: string[]) => {
        const labelTextures: LabelTextureDataVO[] = [];
        for (const requestId of requestIds) {
          const requestEntry: RequestEntry = this.requestMap.getValue(requestId);
          const labelTexture: LabelTextureDataVO = requestEntry.textureData;
          if (ArrayUtils.isNotEmpty(requestEntry.cloneRequests)) {
            for (const cloneRequestData of requestEntry.cloneRequests) {
              const labelTextureData: LabelTextureDataVO = new LabelTextureDataVO();
              labelTextureData.id = cloneRequestData.requestId;
              labelTextureData.identity = cloneRequestData.identity;
              labelTextureData.textureData = labelTexture.textureData;
              labelTextures.push(labelTextureData);
            }
          }
          labelTextures.push(labelTexture);
          this.requestMap.remove(requestId);
        }
        return labelTextures;
      }),
      catchError((error: any) => {
        const requestIds: string[] = requestIdToRequestMapping.keys();
        for (const requestId of requestIds) {
          this.requestMap.remove(requestId);
        }
        return of([]);
      })
    );
  }

  private addTextureDataMapping(request: LabelTextureRequestVO, dataMapping: CloneRequestData): void {
    this.checkRequestEntry(request);
    this.requestMap.getValue(request.id).cloneRequests.push(dataMapping);
  }

  private findCachedLabelTextureData(labelTextureRequest: Request): string {
    if (!this.isCacheEnabled) {
      return null;
    }
    try {
      const textureHashCode: string = this.calculateTextureHashCode(labelTextureRequest.labelDescriptions);
      const data: string = this.storage.getItem(textureHashCode);
      if (data) {
        const cacheEntry: TextureCacheEntry = this.modelConversionService.convertTextureCacheEntry(JSON.parse(data));
        if (ArrayUtils.equals(labelTextureRequest.labelDescriptions, cacheEntry.labelTextureRequest.labelTextureDescriptions)) {
          return cacheEntry.textureData;
        }
      }
    } catch (error) {
      Log.error('Error: TextureCacheManager: error occurred during searching for the texture in the storage: ', error);
    }
  }

  private addToCache(labelTextureRequest: LabelTextureRequestVO, labelTextureData: LabelTextureDataVO): RequestEntry {
    this.checkRequestEntry(labelTextureRequest);
    const requestEntry: RequestEntry = this.requestMap.getValue(labelTextureRequest.id);
    requestEntry.textureData = labelTextureData;
    if (this.isCacheEnabled) {
      const textureHashCode: string = this.calculateTextureHashCode(labelTextureRequest.labelTextureDescriptions);
      const cacheEntry: TextureCacheEntry = new TextureCacheEntry();
      cacheEntry.labelTextureRequest = labelTextureRequest;
      cacheEntry.textureData = labelTextureData.textureData;
      this.storage.setItem(textureHashCode, JSON.stringify(cacheEntry));
    }
    return requestEntry;
  }

  private checkRequestEntry(labelTextureRequest: LabelTextureRequestVO): void {
    if (!this.requestMap.containsKey(labelTextureRequest.id)) {
      const requestEntry: RequestEntry = new RequestEntry();
      requestEntry.textureRequest = labelTextureRequest;
      this.requestMap.setValue(labelTextureRequest.id, requestEntry);
    }
  }

  private calculateTextureHashCode(textureData: LabelTextureDescriptionVO[]): string {
    if (ArrayUtils.isEmpty(textureData)) {
      return '';
    }

    this.roundingValues(textureData);
    const data: string = JSON.stringify(textureData);
    let hash: number = 0;
    let i: number;
    let chr: number;
    if (data.length === 0) {
      return String(hash);
    }
    for (i = 0; i < data.length; i++) {
      chr = data.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = (hash << 5) - hash + chr;
      // eslint-disable-next-line no-bitwise
      hash |= 0;
    }
    return String(hash);
  }

  private roundingValues(textureData: LabelTextureDescriptionVO[]): void {
    textureData.forEach((data: LabelTextureDescriptionVO) => {
      data.height = MathUtils.roundNumberValue(data.height);
      data.width = MathUtils.roundNumberValue(data.width);
      data.distanceToPreviousConnection = MathUtils.roundNumberValue(data.distanceToPreviousConnection);
    });
  }
}
